import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAmendmentCreateEditDispatch, useAmendmentCreateEditState, ACTION_TYPES } from './amendment-create-edit-context';
import ContractAppService from '../../../../application/platform/scp/contract/contract-app-service';
import { useBaseLayout } from '../../../../components/context/use-base-layout';

export const useAmendmentCreateEditController = () => {
    const state = useAmendmentCreateEditState();
    const dispatch = useAmendmentCreateEditDispatch();
    const navigate = useNavigate();
    const { showMessage } = useBaseLayout();

    const fieldMap: Record<string, string> = {
        'input-additional-information': 'additionalInformation',
        'input-initial-value': 'initialValue',
        'input-increase-value': 'increaseValue',
        'input-decrease-value': 'decreaseValue',
        'input-amendment-type-id': 'amendmentTypeId',
        'select-amendment-type': 'amendmentTypeId',
        'input-amendment-number': 'amendmentNumber',
        'input-amendment-object': 'amendmentObject',
        'input-qualification-addition-suppression': 'qualificationAdditionSuppression',
        'input-qualification-validity': 'qualificationValidity',
        'input-qualification-supplier': 'qualificationSupplier',
        'input-qualification-informative': 'qualificationInformative',
        'input-qualification-readjustment': 'qualificationReadjustment',
        'check-qualification-addition-suppression': 'qualificationAdditionSuppression',
        'check-qualification-validity': 'qualificationValidity',
        'check-qualification-supplier': 'qualificationSupplier',
        'check-qualification-informative': 'qualificationInformative',
        'check-qualification-readjustment': 'qualificationReadjustment',
        'input-supplier-number-identification': 'supplierNumberIdentification',
        'input-supplier-type': 'supplierType',
        'select-supplier-type': 'supplierType',
        'input-supplier-name': 'supplierName',
        'input-supplier-number-identification-sub-contract': 'supplierNumberIdentificationSubContract',
        'input-supplier-type-sub-contract': 'supplierTypeSubContract',
        'input-supplier-name-sub-contract': 'supplierNameSubContract',
        'input-informative-observation': 'informativeObservation',
        'input-legal-basis': 'legalBasis',
        'input-number-installments': 'numberInstallments',
        'input-installment-value': 'installmentValue',
        'input-global-value': 'globalValue',
        'input-additional-deadline-days': 'additionalDeadlineDays',
        'input-justification': 'justification',
    };

    const handleOnChange = (e: any) => {
        const { name, value, checked, type } = e.target;
        const fieldName = fieldMap[name] || name;

        if (type === 'checkbox') {
            dispatch({
                type: ACTION_TYPES.setAmendmentRequest,
                payload: { [fieldName]: checked }
            });
        } else {
            dispatch({
                type: ACTION_TYPES.setAmendmentRequest,
                payload: { [fieldName]: value }
            });
        }
    };

    const handleDateChange = (field: string, date: any) => {
        if (date && date.isValid && date.isValid()) {
            dispatch({
                type: ACTION_TYPES.setAmendmentRequest,
                payload: { [field]: date.format('YYYY-MM-DD') }
            });
        } else {
            dispatch({
                type: ACTION_TYPES.setAmendmentRequest,
                payload: { [field]: undefined }
            });
        }
    };

    const validFields = (): boolean => {
        if (!state.amendmentRequest.amendmentTypeId) {
            return false;
        }
        if (!state.amendmentRequest.amendmentNumber || state.amendmentRequest.amendmentNumber.length === 0) {
            return false;
        }
        if (!state.amendmentRequest.signDate || state.amendmentRequest.signDate.length === 0) {
            return false;
        }
        if (!state.amendmentRequest.amendmentObject || state.amendmentRequest.amendmentObject.length === 0) {
            return false;
        }
        if (!state.amendmentRequest.supplierType) {
            return false;
        }
        if (!state.amendmentRequest.supplierNumberIdentification || state.amendmentRequest.supplierNumberIdentification.length === 0) {
            return false;
        }
        if (!state.amendmentRequest.supplierName || state.amendmentRequest.supplierName.length === 0) {
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!validFields()) {
            dispatch({
                type: ACTION_TYPES.setShowValidationErrors,
                payload: true
            });
            showMessage({
                open: true,
                message: 'Preencha todos os campos obrigatórios',
                type: 'warning'
            });
            return;
        }

        try {
            if (state.sequentialId) {
                await ContractAppService.updateContractAmendment(
                    Number(state.contractId),
                    Number(state.sequentialId),
                    state.amendmentRequest
                );
                showMessage({
                    open: true,
                    message: 'Aditivo atualizado com sucesso',
                    type: 'success'
                });
                navigate(`/contratos/${state.contractId}/aditivos`);
            } else {
                await ContractAppService.addContractAmendment(
                    Number(state.contractId),
                    state.amendmentRequest
                );
                showMessage({
                    open: true,
                    message: 'Aditivo criado com sucesso',
                    type: 'success'
                });
                // Abre o diálogo apenas ao criar um novo aditivo
                dispatch({
                    type: ACTION_TYPES.setShowConfirmDialog,
                    payload: true
                });
            }
        } catch (error: any) {
            showMessage({
                open: true,
                message: 'Não foi possível salvar o aditivo',
                detail: (error as any).details || error.message,
                type: 'error'
            });
        }
    };

    const handleConfirmAddAnother = () => {
        // Reseta o formulário e fecha o diálogo
        dispatch({ type: ACTION_TYPES.resetState });
        dispatch({ type: ACTION_TYPES.setShowConfirmDialog, payload: false });
        dispatch({ type: ACTION_TYPES.setShowValidationErrors, payload: false });
    };

    const handleCancelAddAnother = () => {
        // Fecha o diálogo e navega para listagem
        dispatch({ type: ACTION_TYPES.setShowConfirmDialog, payload: false });
        navigate(`/contratos/${state.contractId}/aditivos`);
    };

    const loadInitialData = async () => {
        if (state.sequentialId) {
            try {
                const data = await ContractAppService.getContractAmendment(
                    Number(state.contractId),
                    Number(state.sequentialId)
                );

                dispatch({
                    type: ACTION_TYPES.setAmendmentRequest,
                    payload: {
                        amendmentTypeId: data.amendmentTypeId,
                        amendmentNumber: data.amendmentNumber,
                        amendmentObject: data.amendmentObject,
                        signDate: data.signDate,
                        publicationDate: data.publicationDate,
                        effectiveDateStart: data.effectiveDateStart,
                        effectiveDateEnd: data.effectiveDateEnd,
                        initialValue: data.initialValue,
                        increaseValue: data.increaseValue,
                        decreaseValue: data.decreaseValue,
                        additionalInformation: data.additionalInformation,
                        qualificationAdditionSuppression: data.qualificationAdditionSuppression,
                        qualificationValidity: data.qualificationValidity,
                        qualificationSupplier: data.qualificationSupplier,
                        qualificationInformative: data.qualificationInformative,
                        qualificationReadjustment: data.qualificationReadjustment,
                        supplierNumberIdentification: data.supplierNumberIdentification,
                        supplierType: data.supplierType,
                        supplierName: data.supplierName,
                        supplierNumberIdentificationSubContract: data.supplierNumberIdentificationSubContract,
                        supplierTypeSubContract: data.supplierTypeSubContract,
                        supplierNameSubContract: data.supplierNameSubContract,
                        informativeObservation: data.informativeObservation,
                        legalBasis: data.legalBasis,
                        numberInstallments: data.numberInstallments,
                        installmentValue: data.installmentValue,
                        globalValue: data.globalValue,
                        additionalDeadlineDays: data.additionalDeadlineDays,
                    }
                });
            } catch (error: any) {
                showMessage({
                    open: true,
                    message: 'Não foi possível carregar os dados do aditivo',
                    detail: (error as any).details || error.message,
                    type: 'error'
                });
            }
        }
    };

    useEffect(() => {
        loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        amendmentRequest: state.amendmentRequest,
        showValidationErrors: state.showValidationErrors,
        showConfirmDialog: state.showConfirmDialog,
        handleOnChange,
        handleDateChange,
        handleSave,
        handleConfirmAddAnother,
        handleCancelAddAnother,
        validFields,
    };
};
